import React, { ElementType, ReactElement } from 'react';
// biome-ignore lint/style/noRestrictedImports: Text wraps FormattedMessage
import { FormattedMessage } from 'react-intl';
import randomString from '@/constants/randomString';

type TFormattedMessageProps = React.ComponentProps<typeof FormattedMessage>;

export const isText = (
  element: unknown
): element is ReactElement<TTextProps> => {
  return React.isValidElement(element) && element.type === Text;
};

export type TTextProps = Omit<
  TFormattedMessageProps,
  'id' | 'defaultMessage' | 'children'
> & {
  as?: ElementType;
  defaultMessage?: string;
} & ({ id: string; children?: never } | { children: string; id?: never });

/**
 * @component Text
 * @description The `Text` component is a wrapper around the `FormattedMessage` component from `react-intl`. It is used to display internationalized messages.
 *
 * @example
 * ```tsx
 * <Text>This text will be localized</Text>
 * <Text
 *  id="Hello, {name}!"
 *  values={{ name: 'John' }}
 * />
 * ```
 *
 * @props
 * @param {ElementType} [as] - Optional prop to specify the type of element to render as.
 * @param {string} [id] - The ID of the message to be displayed.
 * @param {string} [children] - The ID of the message to be displayed.
 * @param {Record<string, ReactNode>} [values] - Optional values to be interpolated into the message.
 * @param {string} [defaultMessage=randomString] - The default message to be displayed if the message ID is not found.
 *
 * @storybook
 * https://storybook.hireology.com/?path=/docs/common-text--docs
 *
 * @notes
 * Note: Ensure that either the `id` or `children` prop is provided. The `children` prop should be a valid message ID that exists in your message catalog.
 */
const Text = ({
  as: Component,
  children,
  defaultMessage = randomString,
  id,
  ...rest
}: TTextProps) => {
  return Component ? (
    <Component>
      <FormattedMessage
        id={id || children}
        defaultMessage={defaultMessage}
        {...rest}
      />
    </Component>
  ) : (
    <FormattedMessage
      id={id || children}
      defaultMessage={defaultMessage}
      {...rest}
    />
  );
};

export default Text;
